import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Map as MapIcon, MapPinOff, Store } from 'lucide-react';
import { WorldPageHeader, WorldSection } from './WorldPageHeader';
import { NpcShopCard } from './NpcShopCard';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { EventChip } from './EventChip';
import { Disclosure } from './Disclosure';
import { CopyLinkButton } from '../guide/CopyLinkButton';
import { useWorld } from '../../contexts/WorldDataContext';
import { npcs } from '../../data/guide/npcs';
import { resolveNpcLocation } from '../../utils/npcLocation';
import { imageEntryForMonster } from '../../utils/entityImages';
import { coordLabel, locationCopyText, mapName, mapPath } from '../../utils/worldFormat';
import type { Monster, WorldData } from '../../types/world';
const btn = 'inline-flex h-9 items-center gap-1.5 rounded-xl border border-white/10 px-3 text-xs font-semibold text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/40 hover:text-white';

/**
 * Ficha pública de un NPC. Misma ruta y mismo ID que antes, sin bloques de caza:
 * sin nivel, spots de farmeo, drops, HP ni estadísticas de combate. Los datos originales siguen en los archivos.
 */
export function NpcEntityView({
  w,
  m
}: {
  w: WorldData;
  m: Monster;
}) {
  const world = useWorld();
  const guideNpcs = npcs.filter(n => n.monsterId === m.id);
  const entry = imageEntryForMonster(w, m.id);
  const title = guideNpcs.length === 1 ? guideNpcs[0].name : m.name;
  useEffect(() => {
    document.title = `${title} · NPC y tiendas · Guía MU Vanguard`;
  }, [title]);
  const spawns = w.idx.spawnsByMonster.get(m.id) ?? [];
  const locations = w.idx.locationsByMonster.get(m.id) ?? [];
  return <div data-id="element-1575" data-mp="190xrbkfw5t4m">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'NPC y tiendas',
      to: '/guia/npc'
    }, {
      label: title
    }]} eyebrow="NPC" title={title} media={entry?.image ? <EntityImage entry={entry} alt={title} name={title} size="hero" lazy={false} kind="persona" data-id="element-1577" data-mp="2kk0jrsx3vhh3" /> : undefined} data-id="element-1576" data-mp="190o1bwuujlyx">
        <p className="mt-3 max-w-2xl text-sm text-gray-300" data-id="element-1578" data-mp="fhcy8d9d1mvd">
          {guideNpcs.length ? guideNpcs.map(n => n.role).filter((v, i, a) => a.indexOf(v) === i).join(' · ') : 'Función no documentada en los datos de Vanguard.'}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-1579" data-mp="pfiykqfiqky4">
          <CopyLinkButton label="Copiar enlace" data-id="element-1580" data-mp="1ox2zympat50" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-1581" data-mp="3svzuysad53ga">
        {guideNpcs.length > 0 ? <WorldSection id="servicios" title={guideNpcs.length > 1 ? 'Ubicaciones y servicios' : 'Ubicación y servicios'} count={guideNpcs.length > 1 ? guideNpcs.length : undefined} data-id="element-1582" data-mp="3stn57syd5oso">
            <div className="grid gap-6 md:grid-cols-2" data-id="element-1583" data-mp="5pqpcnkhvzvs">
              {guideNpcs.map(n => <NpcShopCard key={n.id} npc={n} loc={resolveNpcLocation(n, {
            status: world.status,
            data: world.data,
            error: world.error
          })} w={w} data-id="element-1584" data-mp="3e50opnx02qzv" />)}
            </div>
          </WorldSection> : <>
            <WorldSection id="ubicacion" title="Dónde está" count={spawns.length || undefined} data-id="element-1585" data-mp="34s1m17ck1mxb">
              {spawns.length ? <ul className="grid gap-2.5 md:grid-cols-2" data-id="element-1586" data-mp="2q3x2y29lb9e0">
                  {spawns.map(s => {
              const coords = coordLabel(s);
              const mn = mapName(w, s.mapId);
              return <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-white/10 p-3.5" data-id="element-1587" data-mp="3iskpe162s4t5">
                        <span className="min-w-0" data-id="element-1588" data-mp="38tvguz02jwpk">
                          <span className="block font-semibold text-white" data-id="element-1589" data-mp="38tdedkmtcm97">{mn}</span>
                          <span className="block text-sm text-gray-400" data-id="element-1590" data-mp="3dreupxsuu7gz">{coords ?? 'Sin coordenadas en los archivos'} · aparición configurada en los archivos</span>
                          {s.eventId && <span className="mt-1 block" data-id="element-1591" data-mp="22dt74h2n8dxk"><EventChip w={w} eventId={s.eventId} data-id="element-1592" data-mp="22f4awgw39loo" /></span>}
                        </span>
                        <span className="flex flex-wrap gap-2" data-id="element-1593" data-mp="2im8xry9wzo6c">
                          <Link to={mapPath(s.mapId)} className={btn} data-id="element-1594" data-mp="2dtpfwp4726vq">
                            <MapIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1595" data-mp="28uw5x6o48y0b" /> Ver mapa
                          </Link>
                          {coords && <CopyTextButton text={locationCopyText(mn, m.name, s)} label="Copiar ubicación" data-id="element-1596" data-mp="23wuloieyesn9" />}
                        </span>
                      </li>;
            })}
                </ul> : locations.length ? <ul className="flex flex-wrap gap-2" data-id="element-1597" data-mp="2rldvx3hkdfhz">
                  {locations.map(l => <li key={l.id} data-id="element-1598" data-mp="2mmavjwcj3xgc">
                      <Link to={mapPath(l.mapId)} className={btn} data-id="element-1599" data-mp="2ml9cp5p9u6o2">
                        <MapIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1600" data-mp="1g5yyxzpcunmv" /> {mapName(w, l.mapId)}
                      </Link>
                    </li>)}
                </ul> : <p className="flex items-start gap-2 rounded-2xl border border-white/10 p-4 text-gray-300" data-id="element-1601" data-mp="3muzgcggm3r1u">
                  <MapPinOff className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1602" data-mp="3rsskz529od00" />
                  Ubicación no documentada en los archivos disponibles.
                </p>}
            </WorldSection>

            <WorldSection id="servicios" title="Servicios" data-id="element-1603" data-mp="5fal5mtio2g0">
              <p className="flex items-start gap-2 text-gray-300" data-id="element-1604" data-mp="adu7vpfxpi9f">
                <Store className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1605" data-mp="eenxqocxt74y" />
                Función y servicios pendientes de documentar en Vanguard. Qué vende se mostrará cuando tengamos su inventario.
              </p>
              <Link to="/guia/npc" className="mt-3 inline-flex font-semibold text-[#D4AF37] underline-offset-4 hover:underline" data-id="element-1606" data-mp="obslgsmflanv">Ver NPC y tiendas</Link>
            </WorldSection>
          </>}

        {entry?.image?.origin === 'paquete' && <Disclosure title="Procedencia de la imagen" data-id="element-1607" data-mp="2q61md0gorx5q">
            <p className="text-sm text-gray-300" data-id="element-1608" data-mp="2i4ag7rdtcydc">
              Foto de referencia visual del personaje (paquete {entry.key.startsWith('paquete-npc') ? 'VANGUARD_NPC' : 'VANGUARD_MOBS_LIMPIOS'}). Identifica al NPC; no es una captura de Vanguard ni confirma ubicación, inventario, precios ni que esté activo.
              Las fotos de Fanz se limpiaron con IA y pueden tener pequeñas diferencias de detalle.
            </p>
          </Disclosure>}
      </div>
    </div>;
}